"use client";

import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Download } from "lucide-react";
import { exportToExcel } from "@/lib/excelExport";
import { formatCurrency } from "@/lib/utils";
import { format } from "date-fns";

interface StaffInfo {
  id: string;
  name: string;
}

interface CashCollectionDashboardProps {
  data: {
    dateRange: {
      start: string;
      end: string;
    };
    staff: StaffInfo[];
    dailyData: Array<{
      date: string;
      byStaff: { [staffName: string]: { orders: number; amount: number } };
      total: { orders: number; amount: number };
    }>;
    totals: {
      byStaff: { [staffName: string]: { orders: number; amount: number } };
      total: { orders: number; amount: number };
    };
  };
}

const COLORS = [
  "#0ea5e9",
  "#8b5cf6",
  "#f59e0b",
  "#10b981",
  "#ef4444",
  "#6366f1",
  "#ec4899",
  "#14b8a6",
];

export function CashCollectionDashboard({
  data,
}: CashCollectionDashboardProps) {
  const staffNames = data.staff.map((s) => s.name);

  const handleExport = () => {
    const exportData = data.dailyData.map((row) => {
      const rowData: Record<string, string | number> = {
        Date: format(new Date(row.date), "dd/MM/yyyy"),
      };
      staffNames.forEach((name) => {
        const staffData = row.byStaff[name] || { orders: 0, amount: 0 };
        rowData[`${name} Orders`] = staffData.orders;
        rowData[`${name} Amount`] = staffData.amount;
      });
      rowData["Total Orders"] = row.total.orders;
      rowData["Total Amount"] = row.total.amount;
      return rowData;
    });

    // Add totals row
    const totalsRow: Record<string, string | number> = { Date: "TOTAL" };
    staffNames.forEach((name) => {
      const staffData = data.totals.byStaff[name] || { orders: 0, amount: 0 };
      totalsRow[`${name} Orders`] = staffData.orders;
      totalsRow[`${name} Amount`] = staffData.amount;
    });
    totalsRow["Total Orders"] = data.totals.total.orders;
    totalsRow["Total Amount"] = data.totals.total.amount;
    exportData.push(totalsRow);

    exportToExcel(exportData, "Cash_Collection", "Cash Collection");
  };

  // Transform data for chart
  const chartData = data.dailyData.map((row) => {
    const chartRow: Record<string, string | number> = { date: row.date };
    staffNames.forEach((name) => {
      chartRow[name] = row.byStaff[name]?.amount || 0;
    });
    return chartRow;
  });

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        {staffNames.slice(0, 3).map((name) => (
          <Card key={name}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{name}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(data.totals.byStaff[name]?.amount || 0)}
              </div>
              <p className="text-xs text-muted-foreground">
                {data.totals.byStaff[name]?.orders || 0} orders
              </p>
            </CardContent>
          </Card>
        ))}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              Total Collection
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(data.totals.total.amount)}
            </div>
            <p className="text-xs text-muted-foreground">
              {data.totals.total.orders} total orders
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Daily Cash Collection by Staff</CardTitle>
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export to Excel
          </Button>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(value) => format(new Date(value), "dd MMM")}
              />
              <YAxis />
              <Tooltip
                labelFormatter={(value) =>
                  format(new Date(value), "dd MMM yyyy")
                }
                formatter={(value: number) => formatCurrency(value)}
              />
              <Legend />
              {staffNames.map((name, index) => (
                <Bar
                  key={name}
                  dataKey={name}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardHeader>
          <CardTitle>Daily Cash Collection Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  {staffNames.map((name) => (
                    <TableHead
                      key={name}
                      colSpan={2}
                      className="text-center border-l"
                    >
                      {name}
                    </TableHead>
                  ))}
                  <TableHead colSpan={2} className="text-center border-l">
                    Total
                  </TableHead>
                </TableRow>
                <TableRow>
                  <TableHead></TableHead>
                  {staffNames.map((name) => (
                    <React.Fragment key={`header-${name}`}>
                      <TableHead className="text-right border-l">
                        Orders
                      </TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </React.Fragment>
                  ))}
                  <TableHead className="text-right border-l">Orders</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.dailyData.map((row) => (
                  <TableRow key={row.date}>
                    <TableCell>
                      {format(new Date(row.date), "dd MMM yyyy")}
                    </TableCell>
                    {staffNames.map((name) => (
                      <React.Fragment key={`row-${row.date}-${name}`}>
                        <TableCell className="text-right border-l">
                          {row.byStaff[name]?.orders || 0}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(row.byStaff[name]?.amount || 0)}
                        </TableCell>
                      </React.Fragment>
                    ))}
                    <TableCell className="text-right border-l">
                      {row.total.orders}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(row.total.amount)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-muted/50 font-bold">
                  <TableCell>TOTAL</TableCell>
                  {staffNames.map((name) => (
                    <React.Fragment key={`total-${name}`}>
                      <TableCell className="text-right border-l">
                        {data.totals.byStaff[name]?.orders || 0}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(data.totals.byStaff[name]?.amount || 0)}
                      </TableCell>
                    </React.Fragment>
                  ))}
                  <TableCell className="text-right border-l">
                    {data.totals.total.orders}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(data.totals.total.amount)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
